/** Ordered access levels, lowest to highest. */
const ACCESS_ORDER = ['none', 'view', 'edit', 'full'];
/** Numeric rank for an access level (none=0 < view=1 < edit=2 < full=3). */
export function accessRank(access) {
    const rank = ACCESS_ORDER.indexOf(access);
    return rank < 0 ? 0 : rank;
}
/**
 * Compare two access levels.
 * Returns >0 if a is higher, <0 if lower, 0 if equal.
 */
export function compareAccess(a, b) {
    return accessRank(a) - accessRank(b);
}
/** The higher of two access levels. */
export function maxAccess(a, b) {
    return compareAccess(a, b) >= 0 ? a : b;
}
/**
 * Normalize a CSV / claim role name to a canonical role id.
 * "Front Desk" -> "front_desk", "Admin" -> "admin".
 */
export function normalizeRole(role) {
    return role
        .trim()
        .toLowerCase()
        .replace(/[\s/-]+/g, '_');
}
/**
 * Normalize a CSV access value to an AccessLevel.
 * Accepts: Full/Edit/View/No (any case). Throws on unknown values.
 */
export function normalizeAccess(value) {
    const v = value.trim().toLowerCase();
    switch (v) {
        case 'full':
            return 'full';
        case 'edit':
            return 'edit';
        case 'view':
            return 'view';
        case 'no':
        case 'none':
        case '':
            return 'none';
        default:
            throw new Error(`Unknown access value: "${value}"`);
    }
}
/**
 * Build the canonical permission key for a module + sub-module.
 * Case- and whitespace-insensitive so CSV headings and UI metadata align.
 */
export function permissionKey(module, subModule) {
    const norm = (s) => s.trim().toLowerCase().replace(/\s+/g, ' ');
    return `${norm(module)}|${norm(subModule)}`;
}
/**
 * Merge multiple permission sets, taking the highest access for each key.
 * Used to combine several role documents for a multi-role user.
 *
 * Logs a development warning when the same key appears with conflicting
 * access levels (duplicate rows resolving to the highest).
 */
export function mergePermissions(sets) {
    const merged = {};
    for (const set of sets) {
        for (const [key, access] of Object.entries(set)) {
            const existing = merged[key];
            if (existing === undefined) {
                merged[key] = access;
                continue;
            }
            if (compareAccess(access, existing) !== 0) {
                warnDev(`Duplicate permission for "${key}": ${existing} vs ${access} — resolving to highest.`);
            }
            merged[key] = maxAccess(existing, access);
        }
    }
    return merged;
}
/** The access level a set grants for a module/sub-module (missing = none). */
export function accessFor(set, module, subModule) {
    return set[permissionKey(module, subModule)] ?? 'none';
}
/**
 * True if the set satisfies the requirement (grants >= minimumAccess,
 * default 'view'). Missing permission = none, so fails closed.
 */
export function canAccess(set, req) {
    const minimum = req.minimumAccess ?? 'view';
    if (minimum === 'none')
        return true;
    return compareAccess(accessFor(set, req.module, req.subModule), minimum) >= 0;
}
export function canView(set, module, subModule) {
    return canAccess(set, { module, subModule, minimumAccess: 'view' });
}
export function canEdit(set, module, subModule) {
    return canAccess(set, { module, subModule, minimumAccess: 'edit' });
}
export function canFull(set, module, subModule) {
    return canAccess(set, { module, subModule, minimumAccess: 'full' });
}
/** True if the set grants at least `view` on any module. */
export function hasAnyAccessibleModule(set) {
    return Object.values(set).some((access) => compareAccess(access, 'view') >= 0);
}
/** Emit a warning only in development (no-op in production builds). */
function warnDev(message) {
    const env = typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : undefined;
    if (env !== 'production') {
        console.warn(`[authz] ${message}`);
    }
}
